import { Injectable } from "@angular/core";
import { Observable, of, forkJoin } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AlertService } from "./alert.service";
import { TranslationService as ApiTranslationService } from "../../api/services/translation.service";
import { TranslateValue$Json$Params } from "../../api/fn/translation/translate-value-json";
import {
  TranslationLanguage,
  TranslationResult,
  TranslationResults,
  TRANSLATION_LANGUAGES,
  LanguageConfig,
} from "../types/translation.types";

/**
 * Service for handling text translations via backend OpenAI endpoint.
 *
 * Provides methods for:
 * - Single text translation to one target language
 * - Parallel translation to multiple target languages using forkJoin
 */
@Injectable({
  providedIn: "root",
})
export class TranslationService {
  constructor(
    private apiTranslationService: ApiTranslationService,
    private alertService: AlertService
  ) {}

  /**
   * Get all available translation languages
   */
  getAvailableLanguages(): readonly LanguageConfig[] {
    return TRANSLATION_LANGUAGES;
  }

  /**
   * Get language configuration by code
   */
  getLanguageConfig(code: TranslationLanguage): LanguageConfig | undefined {
    return TRANSLATION_LANGUAGES.find((l) => l.code === code);
  }

  /**
   * Translate text to a single target language
   *
   * @param text - Source text to translate
   * @param targetLanguage - Target language code
   * @param sourceLanguage - Optional source language code (defaults to 'nl')
   * @returns Observable with translation result
   */
  translateSingle(
    text: string,
    targetLanguage: TranslationLanguage,
    sourceLanguage: TranslationLanguage = "nl"
  ): Observable<TranslationResult> {
    // Validate input
    if (!text || text.trim().length === 0) {
      return of({
        translatedText: "",
        targetLanguage,
        success: false,
        error: "No text provided for translation",
      });
    }

    // Make API call using the generated API service
    const params: TranslateValue$Json$Params = {
      Content: text,
      FromLanguage: sourceLanguage,
      ToLanguage: targetLanguage,
    };

    return this.apiTranslationService.translateValue$Json(params).pipe(
      map((response) => ({
        translatedText: response || "",
        targetLanguage,
        success: true,
      })),
      catchError((error) => {
        console.error(`Translation to ${targetLanguage} failed:`, error);
        return of({
          translatedText: "",
          targetLanguage,
          success: false,
          error: error.message || "Translation failed",
        });
      })
    );
  }

  /**
   * Translate text to multiple target languages in parallel
   *
   * Uses forkJoin to make parallel API calls for each target language.
   * Each translation is independent - failures in one don't affect others.
   *
   * @param text - Source text to translate
   * @param targetLanguages - Array of target language codes
   * @param sourceLanguage - Optional source language code
   * @returns Observable with aggregated translation results
   */
  translateToMultiple(
    text: string,
    targetLanguages: TranslationLanguage[],
    sourceLanguage?: TranslationLanguage
  ): Observable<TranslationResults> {
    // Validate input
    if (!text || text.trim().length === 0) {
      return of({
        results: targetLanguages.map((lang) => ({
          translatedText: "",
          targetLanguage: lang,
          success: false,
          error: "No text provided for translation",
        })),
        successCount: 0,
        failureCount: targetLanguages.length,
      });
    }

    if (targetLanguages.length === 0) {
      return of({
        results: [],
        successCount: 0,
        failureCount: 0,
      });
    }

    // Create parallel translation observables
    // Each observable handles its own errors via catchError
    const translations$ = targetLanguages.map((lang) =>
      this.translateSingle(text, lang, sourceLanguage).pipe(
        catchError((error) =>
          of({
            translatedText: "",
            targetLanguage: lang,
            success: false,
            error: error.message || "Translation failed",
          })
        )
      )
    );

    // Execute all translations in parallel
    return forkJoin(translations$).pipe(
      map((results) => {
        const successCount = results.filter((r) => r.success).length;
        const failureCount = results.length - successCount;

        return {
          results,
          successCount,
          failureCount,
        };
      })
    );
  }

  /**
   * Get languages that can be translated to (excluding source language)
   *
   * @param sourceLanguage - Current source language to exclude
   * @returns Array of available target languages
   */
  getTargetLanguages(sourceLanguage: TranslationLanguage): LanguageConfig[] {
    return TRANSLATION_LANGUAGES.filter((l) => l.code !== sourceLanguage);
  }

  /**
   * Check if a language has content (non-empty string)
   *
   * @param content - Content to check
   * @returns true if content is non-empty
   */
  hasContent(content: string | null | undefined): boolean {
    return (
      content !== null && content !== undefined && content.trim().length > 0
    );
  }

  /**
   * Strip HTML tags from text for translation
   * Preserves the text content while removing HTML structure
   *
   * @param html - HTML string
   * @returns Plain text without HTML tags
   */
  stripHtml(html: string): string {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }
}
